import React, {forwardRef, useMemo, useReducer} from 'react';
import {useSelector} from 'react-redux';

import {blockLastResultSelector} from '../../../store/reducers/blockResultReducer';
import {getBlockSelector} from '../../../store/reducers/blockReducer';
import {BLOCK_TYPE, defaultContentActions, defaultContentOptions} from '../constants';
import {BaseBlock, ContentActions, ContentOptions} from '../types';
import {contentReducer, setContentFormKeyValue, setContentFormKeysValues} from '../store';
import {initialKPICardContentState} from './store';
import {useCreateBlock, useInitBlock, useUpdateBlockContentJson} from '../hooks';
import {ContentCard, ContentToolbar} from '../components';
import {contentToSelectOption, createDefaultContent} from './utils';
import NoSourceBlock from './NoSourceBlock';
import KPICardForm from './KPICardForm';
import ExportOptions from './ExportOptions';

import './KPICard.scss';

interface KPICardProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const KPICard = forwardRef(({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: KPICardProps, ref: any) => {
  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const [contentState, contentDispatch] = useReducer(contentReducer, initialKPICardContentState);
  const uid = content.uid;
  const block = useSelector(getBlockSelector(uid as string));
  const allBlocks = useSelector((state: any) => state.block.blocks);
  const sourceBlocks = allBlocks
    .filter((b: any) => b.type === BLOCK_TYPE.SQL &&
    !['condition_check', 'mail_merge'].includes(b.name)); // TODO - make this dynamic

  const sourceData = useSelector(blockLastResultSelector(contentState.form.source_uid));

  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  const noSourceBlocks = sourceBlocks?.length === 0;

  useCreateBlock(content, createDefaultContent());
  useInitBlock(uid, content, contentDispatch);

  const onKeyValueChange = (key: string) => (value: any) => {
    updateBlockContentJson(key, value);
    contentDispatch(setContentFormKeyValue(key, value));
  };

  const onKeysValuesChange = (keys: string[], values: any[]) => {
    updateBlockContentJson(keys, values);
    contentDispatch(setContentFormKeysValues(keys, values));
  };

  const onSourceChange = (value: any) => {
    const newContent = {
      'source': sourceBlocks.find((b: any) => b.uid === value)?.name || '',
      'source_uid': value,
      'column': '',
      // TODO - reset sparkline and comparison
    }
    const keys = Object.keys(newContent);
    const values = Object.values(newContent);
    onKeysValuesChange(keys, values);
  };

  const onSparklineChange = (key: string) => (value: any) => {
    updateBlockContentJson(`sparkline.${key}`, value);
  }

  /*const onComparisonChange = (key: string) => (value: any) => {
    updateBlockContentJson(`comparison.${key}`, value);
  }*/
  const onComparisonChange = (value: any) => {
    updateBlockContentJson('comparison', value); /* value is whole JSON */
  };

  const onFormatIsPercentageChange = (format: string, isPercentage: boolean) => {
    /*
     * `format` is dependent on `is_percentage`;
     * - if `is_percentage` is true, `format` is appended with `%`
     * - if `is_percentage` is false, we remove `%` from `format`, if any
     */
    const keys = ['format', 'is_percentage'];
    const values = [format, isPercentage];
    onKeysValuesChange(keys, values);
  };

  const onEmbedChange = (value: any) => {
    updateBlockContentJson(`export_options.embed`, value);
  }

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={noSourceBlocks || options.readOnly}
      noContentElement={noSourceBlocks ? <NoSourceBlock/> : undefined}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
    >
      <div className="content__kpi-card">
        <ContentToolbar
          contentId={uid as string}
          selected={contentState.form.source_uid}
          onSelect={onSourceChange}
          options={sourceBlocks.map(contentToSelectOption)}
          updating={updating}
          selectText="Source"
          selectLabel="Select Source"
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false} /* options.showSave / TODO - fix */
          disabled={options.readOnly}
        />
        <div className="content-card__content__form">
          <KPICardForm
            uid={uid as string}
            column={contentState.form.column}
            label={contentState.form.column_label}
            format={contentState.form.format as string}
            isPercentage={contentState.form.is_percentage || false}
            sourceUid={contentState.form.source_uid}
            sourceData={sourceData}
            sparkline={contentState.form.sparkline}
            comparison={contentState.form.comparison}
            onColumnChange={onKeyValueChange('column')}
            onLabelChange={onKeyValueChange('column_label')}
            onFormatIsPercentageChange={onFormatIsPercentageChange}
            onSparklineChange={onSparklineChange}
            onComparisonChange={onComparisonChange}
            disabled={options.readOnly}
          />
        </div>
      </div>
      <ExportOptions
        show={options.showExportOptions as boolean && contentState.form.source_uid !== null}
        block={block}
        onChange={onEmbedChange}
        disabled={options.readOnly}
      />
    </ContentCard>
  )
});
